import React, { useMemo, useState } from 'react';

const useLazyScript = (
    src: string,
    cleanup: boolean = false,
    resourceType: 'script' | 'link',
    setScriptError: React.Dispatch<React.SetStateAction<string>>
) => {
    const [loaded, setLoaded] = useState(false);
    useMemo(() => {
        const date = new Date();
        if (!src) return;

        let element: HTMLScriptElement | HTMLLinkElement | null = document.querySelector(
            `${resourceType}[${resourceType === 'script' ? 'src' : 'href'}="${src}"]`
        );
        let append = false;
        if (!element) {
            element = document.createElement(resourceType) as HTMLScriptElement | HTMLLinkElement;
            if (resourceType === 'script') {
                (element as HTMLScriptElement).src = src;
                (element as HTMLScriptElement).defer = true;
            } else {
                (element as HTMLLinkElement).href = src;
                (element as HTMLLinkElement).rel = 'stylesheet';
            }
            append = true;
        } else if (element.hasAttribute('data-loaded')) {
            setLoaded(true);
            return;
        }
    
        const onLoad = () => {
            console.debug(`${src} loaded`, date.getSeconds(), date.getMilliseconds());
            element.setAttribute('data-loaded', 'true');
            setLoaded(true);
        };
    
        const onError = () => {
            if (cleanup) element.remove();
            setLoaded(false);
            console.error(`${src} failed to load`);
            setScriptError(`Failed to load scripts, try refreshing the page...`);
        };
    
        element.addEventListener('load', onLoad);
        element.addEventListener('error', onError);
    
        if (append){
            resourceType === 'script' ? document.body.appendChild(element) : document.head.appendChild(element);
        }
    
        return () => {
            if (cleanup) element?.remove();
            element?.removeEventListener('load', onLoad);
            element?.removeEventListener('error', onError);
        };

    }, [src, cleanup, resourceType]);

    return loaded;
};

export default useLazyScript;